import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Save, Trash2, RotateCcw, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

function SectionEditor({ s, i, update, move, remove }) {
  return (
    <div className="bg-white border border-border rounded-xl p-5" data-testid={`template-section-${i}`}>
      <div className="flex gap-2 items-center mb-3">
        <Input value={s.title} onChange={(e) => update(i, { title: e.target.value })} className="font-bold" data-testid={`template-title-${i}`} />
        <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-navy p-1" aria-label="Su"><ArrowUp size={16} /></button>
        <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-navy p-1" aria-label="Giù"><ArrowDown size={16} /></button>
        <button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`template-delete-${i}`}><Trash2 size={16} /></button>
      </div>
      <Textarea value={s.body} rows={Math.min(14, Math.max(4, s.body.split("\n").length + 1))} onChange={(e) => update(i, { body: e.target.value })} className="text-sm font-mono" data-testid={`template-body-${i}`} />
      <div className="flex flex-wrap gap-6 mt-3 text-sm text-navy font-semibold">
        <label className="flex items-center gap-2"><Switch checked={s.signature} onCheckedChange={(v) => update(i, { signature: v })} /> Spazio firme dopo la sezione</label>
        <label className="flex items-center gap-2"><Switch checked={s.page_break} onCheckedChange={(v) => update(i, { page_break: v })} /> Nuova pagina</label>
      </div>
    </div>
  );
}

export default function AdminContractTemplate() {
  const [sections, setSections] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);

  useEffect(() => {
    api.get("/contract-template").then(({ data }) => { setSections(data.sections); setPlaceholders(data.placeholders); });
  }, []);

  const update = (i, patch) => setSections(sections.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= sections.length) return;
    const n = [...sections];
    [n[i], n[j]] = [n[j], n[i]];
    setSections(n);
  };
  const remove = (i) => window.confirm("Eliminare questa sezione?") && setSections(sections.filter((_, j) => j !== i));
  const save = async () => {
    try {
      const { data } = await api.put("/contract-template", { sections });
      setSections(data.sections);
      toast.success("Modello contratto salvato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reset = async () => {
    if (!window.confirm("Ripristinare il testo originale del contratto?")) return;
    const { data } = await api.post("/contract-template/reset");
    setSections(data.sections);
    toast.success("Modello ripristinato");
  };

  if (!sections) return <div className="p-10 text-muted-foreground">Caricamento...</div>;
  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h1 className="text-3xl font-extrabold text-navy tracking-tight">Modello contratto</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="template-reset-button" className="gap-2 font-bold"><RotateCcw size={16} /> Ripristina</Button>
          <Button onClick={save} data-testid="template-save-button" className="gap-2 font-bold"><Save size={16} /> Salva</Button>
        </div>
      </div>
      <p className="text-muted-foreground mb-6">Testo del contratto ufficiale. Riga vuota = nuovo paragrafo, **testo** = grassetto. I campi tra graffe si compilano da soli.</p>
      <div className="bg-white border border-dashed border-navy/30 rounded-xl p-4 mb-6" data-testid="template-placeholders">
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Campi automatici</p>
        <div className="flex flex-wrap gap-2">
          {placeholders.map((p) => (
            <button key={p.key} title={p.label} onClick={() => { navigator.clipboard.writeText(`{{${p.key}}}`); toast.success("Copiato"); }}
              className="text-xs font-mono px-2 py-1 rounded bg-secondary text-navy hover:bg-primary/10 transition-colors duration-200">{`{{${p.key}}}`}</button>
          ))}
        </div>
      </div>
      <div className="space-y-4">
        {sections.map((s, i) => <SectionEditor key={i} s={s} i={i} update={update} move={move} remove={remove} />)}
      </div>
      <Button variant="outline" onClick={() => setSections([...sections, { title: "Nuova sezione", body: "", signature: false, page_break: false }])} data-testid="template-add-section" className="gap-2 font-bold mt-4"><Plus size={16} /> Aggiungi sezione</Button>
    </div>
  );
}
